// useFCMDeviceToken.js
import { useEffect, useState } from 'react';
import { messaging } from '../firebase';
import { registerAndGetToken } from '../utils/fcm';
import { useNotification } from '../context/NotificationContext';

export default function useFCMDeviceToken() {
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  useEffect(() => {
    async function fetchToken() {
      // ✅ FIX: Comprehensive capability checks
      if (typeof window === 'undefined') return;

      // Check HTTPS requirement (except localhost)
      const isSecure = window.location.protocol === 'https:';
      const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '::1';

      if (!isSecure && !isLocalhost) {
        console.warn('[FCM] ⚠️ HTTPS required for FCM notifications');
        setError(new Error('HTTPS_REQUIRED'));
        return;
      }

      // Check service worker support
      if (!('serviceWorker' in navigator)) {
        console.warn('[FCM] ⚠️ Service workers not supported');
        setError(new Error('SERVICE_WORKER_NOT_SUPPORTED'));
        return;
      }

      // Check notification API support
      if (!('Notification' in window)) {
        console.warn('[FCM] ⚠️ Notifications API not supported');
        setError(new Error('NOTIFICATIONS_NOT_SUPPORTED'));
        return;
      }

      try {
        // Check notification permission first - SILENT, no toast
        if (Notification.permission === 'denied') {
          const err = new Error('permission-blocked');
          setError(err);
          // Don't show notification for denied permissions - user already made their choice
          console.log('[FCM] 🚫 Permission denied by user');
          return;
        }

        const currentToken = await registerAndGetToken(process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY);
        setToken(currentToken);
        if (currentToken) {
          localStorage.setItem('fcmDeviceToken', currentToken);
          console.log('[FCM] ✅ Token obtained:', currentToken.substring(0, 20) + '...');
        } else {
          console.warn('[FCM] ⚠️ No token received');
        }
      } catch (err) {
        setError(err);
        // SILENT - Don't show notification toast for FCM errors
        // The app works fine without FCM, no need to alarm users
        console.log('[FCM] Setup error (non-critical):', err.message);
      }
    }
    fetchToken();
  }, [showNotification]);

  return { token, error };
}
